import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import Button from '../../components/Button/Button';
import LiquidGlassCard from '../../components/LiquidGlassCard/LiquidGlassCard';
import './Home.css';

const CRYPTO_COINS = [
  { id: 'btc', name: 'Bitcoin', symbol: 'BTC', icon: '₿', color: '#f7931a' },
  { id: 'eth', name: 'Ethereum', symbol: 'ETH', icon: 'Ξ', color: '#627eea' },
  { id: 'usdt', name: 'Tether', symbol: 'USDT', icon: '₮', color: '#26a17b' },
  { id: 'ltc', name: 'Litecoin', symbol: 'LTC', icon: 'Ł', color: '#bfbbbb' },
];

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [balance, setBalance] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const hasFetched = useRef(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get('/v1/products', isAuthenticated());
        if (response?.data) {
          const productList = Array.isArray(response.data) ? response.data : response.data.products || [];
          setProducts(productList);
        }
      } catch (err) {
        console.error('Failed to fetch products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    if (!isAuthenticated() || hasFetched.current) return;
    hasFetched.current = true;

    const fetchBalance = async () => {
      try {
        const response = await api.get('/v1/users', true);
        if (response?.data?.user?.userBalance) {
          setBalance(response.data.user.userBalance);
        }
      } catch (err) {
        console.error('Failed to fetch balance:', err);
      }
    };
    fetchBalance();
  }, [isAuthenticated]);
  const features = [
    {
      icon: 'sell',
      title: 'Free Numbers',
      description: 'Access a wide range of free-to-use numbers for any service.'
    },
    {
      icon: 'lock_open',
      title: 'Public & Private Options',
      description: 'Choose from public numbers or get a dedicated private number for your use.'
    },
    {
      icon: 'public',
      title: 'Global Coverage',
      description: 'Receive SMS from anywhere with our extensive list of international numbers.'
    },
    {
      icon: 'forward_to_inbox',
      title: 'Instant SMS Reception',
      description: 'Your messages appear instantly without any delay.'
    },
    {
      icon: 'no_accounts',
      title: 'No Registration Required',
      description: 'Get started in seconds. No sign-up or personal information needed.'
    },
    {
      icon: 'verified_user',
      title: 'Privacy Focused',
      description: 'Keep your personal number private and protect yourself from spam.'
    }
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <h1 className="hero-title">
                Get a Temporary Phone Number for SMS Verification Instantly 📱
              </h1>
              <p className="hero-description">
                Protect your privacy with our secure, instant, and globally accessible temporary numbers. No registration required.
              </p>
              <div className="hero-actions">
                <Link to="/services">
                  <Button variant="primary" size="large">Get Started</Button>
                </Link>
                <a href="https://play.google.com/store/apps/details?id=com.temp_number.smsify" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="large">Download App</Button>
                </a>
              </div>
            </div>
            <div className="hero-image">
              <img
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuAS8Kn04HpBKxVREO7Cw2-6u_RGapx6XD7jW3MCusB5T7oKfRZm4Ag9h8W-_0rVdpovPoyTKPags_f00Pl7OEhbz22bpWbZNilJkM6bB52856-zr5A9GXnkhs7TVSEKQPooFiOK9XGyvu0sLjEglyOobLlpOWtCEt1naP1WVXUSxCzdbFH1CP7bfCiJqAUZuPp11D-Zv9bA0IdPP20Gat1PtcUavFDSR40_DHaPZSMPR3EV89iArx0yxWDxIbtaReisKwDQBfU7nm4"
                alt="An illustration showing a smartphone receiving a verification code"
                className="hero-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Balance Top Up Section */}
      {isAuthenticated() && balance && (
        <section className="balance-section">
          <div className="container">
            <LiquidGlassCard className="balance-topup-card">
              <div className="balance-topup-content">
                <div className="balance-topup-info">
                  <span className="material-symbols-outlined balance-topup-icon">account_balance_wallet</span>
                  <div>
                    <p className="balance-topup-label">Your Balance</p>
                    <p className="balance-topup-amount">${parseFloat(balance.coins).toFixed(2)}</p>
                  </div>
                </div>
                <div className="balance-topup-actions">
                  <Button variant="primary" onClick={() => navigate('/checkout')}>
                    <span className="material-symbols-outlined">add</span>
                    Top Up Balance
                  </Button>
                  <button className="balance-crypto-btn" onClick={() => navigate('/crypto-payment')}>
                    <span className="balance-crypto-icon">&#8383;</span>
                    Pay with Crypto
                  </button>
                </div>
              </div>
            </LiquidGlassCard>
          </div>
        </section>
      )}

      {/* Buy Coins Section */}
      <section className="buy-coins-section">
        <div className="container">
          <div className="buy-coins-header">
            <h2 className="section-title">Buy Coins</h2>
            <p className="section-description">
              Top up your account with coins to rent phone numbers for SMS verification.
            </p>
          </div>

          {/* Crypto Selection */}
          <div className="buy-coins-crypto-row">
            {CRYPTO_COINS.map((coin) => (
              <div key={coin.id} className="buy-coins-crypto-item">
                <span
                  className="buy-coins-crypto-icon"
                  style={{
                    backgroundColor: `${coin.color}15`,
                    color: coin.color,
                  }}
                >
                  {coin.icon}
                </span>
                <span className="buy-coins-crypto-name">{coin.name}</span>
              </div>
            ))}
          </div>

          {/* Packages */}
          {loadingProducts ? (
            <div className="buy-coins-loading">
              <div className="buy-coins-spinner"></div>
            </div>
          ) : products.length > 0 ? (
            <div className="buy-coins-grid">
              {products.map((product) => (
                <LiquidGlassCard
                  key={product.serviceProductId}
                  hover
                  className="buy-coins-card"
                  onClick={() => navigate('/crypto-payment')}
                >
                  {product.discount > 1 && (
                    <span className="buy-coins-badge">
                      Save {Math.round((1 - 1 / product.discount) * 100)}%
                    </span>
                  )}
                  <div className="buy-coins-amount">
                    <span className="material-symbols-outlined">toll</span>
                    <span>{product.coins.toLocaleString()}</span>
                  </div>
                  <p className="buy-coins-label">coins</p>
                  <div className="buy-coins-price">${product.price.toFixed(2)}</div>
                  <p className="buy-coins-rate">${(product.price / product.coins).toFixed(4)} / coin</p>
                </LiquidGlassCard>
              ))}
            </div>
          ) : (
            <div className="buy-coins-empty">
              <span className="material-symbols-outlined">inventory_2</span>
              <p>No packages available at the moment</p>
            </div>
          )}

          <div className="buy-coins-actions">
            <Button variant="primary" size="large" onClick={() => navigate('/crypto-payment')}>
              <span className="material-symbols-outlined">currency_bitcoin</span>
              Pay with Crypto
            </Button>
            <Button variant="outline" size="large" onClick={() => navigate('/checkout')}>
              <span className="material-symbols-outlined">credit_card</span>
              Pay with Card
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="features-header">
            <h2 className="section-title">Everything You Need for Secure Verification</h2>
            <p className="section-description">
              OpenSMS provides a seamless and secure way to receive SMS online, with features designed for your privacy and convenience.
            </p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <LiquidGlassCard key={index} hover>
                <div className="feature-icon">
                  <span className="material-symbols-outlined">{feature.icon}</span>
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </LiquidGlassCard>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
